import { useMemo } from "react"
import DeleteIcon from "@mui/icons-material/Delete"
import EditIcon from "@mui/icons-material/Edit"
import GroupsIcon from "@mui/icons-material/Groups"
import AddIcon from "@mui/icons-material/Add"
import {
    Alert,
    Box,
    Button,
    Chip,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material"

import {
    DocumentTeamMemberDto,
    DocumentWorkflowStageDto,
} from "../../documentWorkflowApi"

import {
    getStageTypeChipSx,
    roleChipColor,
    stageTypeChipIcons,
} from "../../DocumentWorkflowConfig"
import { LpField } from "../../../../../app/components/LpField"
import { MemberIdentity } from "../../../../../app/components/MemberIdentity"
import { getStageMembers, getStageUiType } from "../../documentWorkflowHelpers"
import { TeamMemberRole, UiStageType } from "../../documentWorkflowTypes"

type TeamSummaryStage = {
    id: number
    label: string
    stageType: UiStageType
}

type TeamSummaryRow = {
    id: number
    name: string
    userName: string
    email: string
    position: string
    companyName: string
    isAuthor: boolean
    roles: TeamMemberRole[]
    stages: TeamSummaryStage[]
}

export type TeamSummaryTableProps = {
    sections: DocumentWorkflowStageDto[]
    teamMembers: DocumentTeamMemberDto[]
    onAddTeamMemberClick: () => void
    onEditTeamMemberClick: (email: string) => void
    onRemoveTeamMember: (email: string) => void
}

const buildSummaryRows = (
    sections: DocumentWorkflowStageDto[],
    teamMembers: DocumentTeamMemberDto[]
): TeamSummaryRow[] => {
    const membersMap = new Map<string, TeamSummaryRow>()
    const authorIds = new Set(
        teamMembers
            .filter(member =>
                member.role === "AUTHOR"
                || member.roles?.includes("AUTHOR")
            )
            .map(member => member.id)
    )
    const stageOrderById = new Map(
        sections.map(section => [section.id, section.order])
    )

    sections.forEach(section => {
        const stageType = getStageUiType(section)
        const stageMembers = getStageMembers(section, teamMembers)
        const stageLabel = `${section.order}. ${section.name}`

        stageMembers.forEach(member => {
            const memberKey = member.userName || member.email
            const existingMember = membersMap.get(memberKey)

            if (existingMember) {
                member.roles.forEach(role => {
                    if (!existingMember.roles.includes(role)) {
                        existingMember.roles.push(role)
                    }
                })

                if (!existingMember.stages.some(stage => stage.id === section.id)) {
                    existingMember.stages.push({
                        id: section.id,
                        label: stageLabel,
                        stageType,
                    })
                }

                return
            }

            membersMap.set(memberKey, {
                id: member.id,
                name: member.name,
                userName: member.userName,
                email: member.email,
                position: member.position,
                companyName: member.companyName,
                isAuthor: authorIds.has(member.id),
                roles: [...member.roles],
                stages: [{
                    id: section.id,
                    label: stageLabel,
                    stageType,
                }],
            })
        })
    })

    return Array.from(membersMap.values())
        .map(member => ({
            ...member,
            stages: [...member.stages].sort((left, right) => {
                return (stageOrderById.get(left.id) ?? Number.MAX_SAFE_INTEGER)
                    - (stageOrderById.get(right.id) ?? Number.MAX_SAFE_INTEGER)
            }),
        }))
        .sort((left, right) => {
            if (left.isAuthor !== right.isAuthor) {
                return left.isAuthor ? -1 : 1
            }

            const stageOrderDifference =
                (stageOrderById.get(left.stages[0]?.id) ?? Number.MAX_SAFE_INTEGER)
                - (stageOrderById.get(right.stages[0]?.id) ?? Number.MAX_SAFE_INTEGER)

            return stageOrderDifference
                || left.name.localeCompare(right.name, "pl")
        })
}

export const TeamSummaryTable = ({
    sections,
    teamMembers,
    onAddTeamMemberClick,
    onEditTeamMemberClick,
    onRemoveTeamMember,
}: TeamSummaryTableProps) => {
    const rows = useMemo(
        () => buildSummaryRows(sections, teamMembers),
        [sections, teamMembers]
    )

    return (
        <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ overflow: "hidden" }}
        >
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell
                            colSpan={5}
                            sx={{
                                bgcolor: "action.hover",
                                borderBottom: "1px solid",
                                borderColor: "divider",
                                py: 1,
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <GroupsIcon
                                    fontSize="medium"
                                    sx={{ color: "text.secondary" }}
                                />
                                <Box>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ fontWeight: 700, lineHeight: 1.2 }}
                                    >
                                        Zespół dokumentu
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {rows.length} {rows.length === 1 ? "osoba" : "osób"}
                                    </Typography>
                                </Box>
                            </Box>
                        </TableCell>
                    </TableRow>

                    <TableRow>
                        <TableCell sx={{ width: 56, textAlign: "center", fontWeight: 600 }}>
                            Lp.
                        </TableCell>
                        <TableCell sx={{ minWidth: 280, fontWeight: 600 }}>
                            Użytkownik
                        </TableCell>
                        <TableCell sx={{ minWidth: 220, fontWeight: 600 }}>
                            Role
                        </TableCell>
                        <TableCell sx={{ minWidth: 320, fontWeight: 600 }}>
                            Etapy
                        </TableCell>
                        <TableCell
                            sx={{ width: 92, textAlign: "center", fontWeight: 600 }}
                        >
                            Akcje
                        </TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {rows.map((member, index) => (
                        <TableRow key={member.userName} hover>
                            <TableCell sx={{ textAlign: "center", fontWeight: 600 }}>
                                <LpField value={index + 1} />
                            </TableCell>

                            <TableCell>
                                <MemberIdentity
                                    name={member.name}
                                    userName={member.userName}
                                    email={member.email}
                                    position={member.position}
                                    companyName={member.companyName}
                                    size="small"
                                />
                            </TableCell>

                            <TableCell>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                                    {member.roles.map(role => (
                                        <Chip
                                            key={role}
                                            label={role}
                                            size="small"
                                            variant="outlined"
                                            color={roleChipColor[role]}
                                        />
                                    ))}
                                </Box>
                            </TableCell>

                            <TableCell>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                                    {member.stages.map(stage => (
                                        <Chip
                                            key={stage.id}
                                            label={stage.label}
                                            icon={stageTypeChipIcons[stage.stageType]}
                                            size="small"
                                            variant="outlined"
                                            sx={getStageTypeChipSx(stage.stageType)}
                                        />
                                    ))}
                                </Box>
                            </TableCell>

                            <TableCell sx={{ textAlign: "center" }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 0.25,
                                    }}
                                >
                                    <Tooltip title="Edytuj przypisania do etapów">
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() => onEditTeamMemberClick(member.email)}
                                            aria-label={`Edytuj przypisania osoby ${member.name}`}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>

                                    <Tooltip title="Usuń z zespołu">
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => onRemoveTeamMember(member.email)}
                                            aria-label={`Usuń osobę ${member.name} z zespołu`}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </TableCell>
                        </TableRow>
                    ))}

                    {rows.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={5}>
                                <Alert severity="info" sx={{ my: 0.5 }}>
                                    Zespół dokumentu jest pusty.
                                </Alert>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    px: 1.5,
                    py: 1,
                    borderTop: "1px solid",
                    borderColor: "divider",
                }}
            >
                <Button
                    variant="contained"
                    color="secondary"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={onAddTeamMemberClick}
                >
                    Dodaj do zespołu
                </Button>
            </Box>
        </TableContainer>
    )
}